import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import HeadlessInput from '../headless/input/HeadlessInput';
import { MemberInfoValue } from '../headless/input/InputBody';
import HeadlessBtn from '../headless/button/HeadlessBtn';
import { PaperBlurFitBtn, BlackFitBtn } from '../headless/button/BtnBody';
import { useModalStore } from '../../store/portal/modalStore';
import { ChangeModalFrame } from '../modal/ChangeModalFrame';
import { getModalMessage } from '../../constants/modalMessages';
import { getBtnName } from '../../constants/btnNames';
import { INPUT_TAG_NAME_MAX_LENGTH } from '../../constants/limits';
import { getAlertMessage } from '../../constants/alertMessages';
import { useValidation } from '../../hooks/useValidation';
import { useStandardAlertStore } from '../../store/portal/alertStore';
import { AlertPosition, AlertType } from '../../enum/EnumRepository';
import { CreateTagAction } from './TagManagementTypes';

interface ModalProps extends CreateTagAction {
  setIsUserInput: (isUserInput: boolean) => void;
}

const CreateTagStationModal: React.FC<ModalProps> = ({
  createTag,
  setIsUserInput,
}) => {
  const { standardModal, isClosing, clearModal } = useModalStore();
  const { enqueueAlert } = useStandardAlertStore();
  const [temp, setTemp] = useState(standardModal?.value || '');
  const [error, setError] = useState('');
  const { validateTagNameInput } = useValidation();

  const validateTagInput = (value: string) => {
    if (value.length === 0) {
      setError('');
      return true;
    }
    const msg = validateTagNameInput(value);
    if (msg) {
      setError(msg);
      return false;
    }
    setError('');
    return true;
  };

  useEffect(() => {
    if (temp.length > 0) {
      setIsUserInput(true);
    } else {
      setIsUserInput(false);
    }
  }, [temp, setIsUserInput]);

  if (standardModal == null) return null;

  const handleApply = async () => {
    if (temp.length === 0) {
      setError(getAlertMessage('MISSING_REQUIRED_FIELD'));
      return;
    }
    if (error !== '') {
      enqueueAlert({
        type: AlertType.ALERT,
        position: AlertPosition.TOP,
        content: getAlertMessage('CHECK_FORMAT'),
      });
      return;
    }
    await createTag({ name: temp, index: 0 });
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const next = e.target.value;
    if (next.length <= INPUT_TAG_NAME_MAX_LENGTH) {
      setTemp(next);
      validateTagInput(next);
    }
  };

  return (
    <ChangeModalFrame onClick={(e) => e.stopPropagation()} $isClosing={isClosing}>
      <ChangeModalComp>
        <Title>{getModalMessage('CREATE_TAG_STATION_TITLE')}</Title>
        <InputContainer>        
          <HeadlessInput
            type="text"
            value={temp}
            handleChange={handleInputChange}
            handleKeyBoard={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                void handleApply();
              }
            }}
            placeholder={getModalMessage('ENTER_FIELD', { field: 'label name' })}
            StyledInput={MemberInfoValue}
          />
          <ErrorText>{ error !== '' ? error : ''}</ErrorText>
        </InputContainer>
        <BtnContainer>
          <HeadlessBtn value={getBtnName('APPLY')} handleClick={handleApply} StyledBtn={BlackFitBtn} />
          <HeadlessBtn value={getBtnName('CANCEL')} handleClick={() => clearModal()} StyledBtn={PaperBlurFitBtn} />
        </BtnContainer>
      </ChangeModalComp>
    </ChangeModalFrame>
  );
};

const ChangeModalComp = styled.div`
  width: 100%;

  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: 2vh 1vw;
`;

const Title = styled.h2`
  margin-bottom: 34px;
  color: ${({ theme }) => theme.colors.color_Gray_02};
  ${({ theme }) => theme.typography.Body_01_1};
`;

const InputContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const BtnContainer = styled.div`
  width: 100%;
  display: flex;
  justify-content: flex-end;
  gap: 1vh;

  margin-top: 2.5vh;
`;

const ErrorText = styled.p`
  width: 100%;
  color: ${({ theme }) => theme.colors.color_alert_red};
  ${({ theme }) => theme.typography.Body_04};
`;

export default CreateTagStationModal;
